// src/pages/ActivityDetails.jsx
import React, { useEffect, useState, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";

import Map from "../../components/Map";
import Search from "../../components/Search";
import UserBadge from "../../components/buttons/UserBadge";
import BackButton from "../../components/buttons/BackButton";
import ActivityTabBar from "../../components/ActivityTabBar";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import it from "date-fns/locale/it";
import iconPlus from "../../assets/icon/general/piu.png";
import ImageUploadCard from "../../components/ImageUploadCard";
import PdfReportLayout from "../../components/utils/download/PdfReportLayout";
import useCensusData from "../../hooks/UserCensusData";
import CensusDataDisplay from "../../components/CensusData";

import ActivitySelector from "./CustomMultiSelect";
import MeansSelector from "./MeansSelector";
import OperatorMentionInput from "../../components/OperatorMetionInput";

import ACTIVITY_OPTIONS from "../../components/utils/ActivityOptions";
import MEANS_OPTIONS from "../../components/utils/MeansOptions";
import api, { getUploadUrl } from "../../Api";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

registerLocale("it", it);

export default function ActivityDetails() {
  /* ---------- contesto & router ---------- */
  const { user } = useContext(UserContext);
  const { id } = useParams();
  const isRup = user?.role === "rup";
  const pdfRef = useRef(null);

  /* ---------- stato base ----------------- */
  const [activity, setActivity] = useState(null);
  const [mapPosition, setMapPosition] = useState(null);
  const [startDateTime, setStartDate] = useState(null);
  const [endDateTime, setEndDate] = useState(null);
  const [creatorUser, setCreatorUser] = useState(null);
  const [assignedUser, setAssignedUser] = useState(null);
  const [notesInput, setNotesInput] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  /* ---------- censimento ----------------- */
  const {
    censusData,
    loading: cLoading,
    error: cError,
  } = useCensusData(activity);
  const [censusDraft, setCensusDraft] = useState(null);
  const [savingCensus, setSavingCensus] = useState(false);

  /* ---------- editing -------------------- */
  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(null); // copia campi editabili
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);

  /* ---------- helper --------------------- */
  const getLabel = (v, arr) => arr.find((o) => o.value === v)?.label ?? v;
  // Solo i RUP non possono modificare le attività, gli altri utenti possono modificare anche quelle chiuse
  const isReadOnly = isRup;


  const toMention = (u) =>
    u ? `@[${u.firstName} ${u.lastName}](${u.id})` : "";

  // Funzioni helper per le azioni del dropdown
  const handleEditActivity = () => {
    setIsEditing(true);
    setShowActionsDropdown(false);
  };

  const handleSaveNotes = () => {
    api
      .patch(`/activity/activities/${id}`, {
        notes: notesInput,
      })
      .then((r) => {
        setActivity(r.data);
        setNotesInput(""); // Svuota la textarea dopo il salvataggio
        toast.success("Note salvate");
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          toast.error("Sessione scaduta. Effettua nuovamente il login.");
          localStorage.removeItem('token');
          window.location.href = '/login';
        } else {
          toast.error("Errore nel salvataggio delle note");
        }
      });
    setShowActionsDropdown(false);
  };

  const handleReopenActivity = () => {
    if (confirm("Vuoi riaprire questa attività?")) {
      updateStatus("open");
    }
    setShowActionsDropdown(false);
  };
  

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionsDropdown && !event.target.closest('.relative')) {
        setShowActionsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActionsDropdown]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/activity/activities/${id}`);
        setActivity(data);
        setMapPosition({ lat: +data.latitude, lng: +data.longitude });
        setStartDate(new Date(data.start_date));
        setEndDate(new Date(data.end_date));
        setNotesInput(data.notes || "");

        setEditDraft({
          activity_names: data.activity_names ?? [],
          means: data.means ?? [],
          priority: data.priority ?? "Media",
          operator_id: data.operator_id ? String(data.operator_id) : "",
          description: data.description ?? "",
        });
      } catch {
        toast.error("Errore nel caricamento dell'attività");
      }
    })();
  }, [id]);

  /* utenti */
  useEffect(() => {
    if (activity?.assigned_user_id)
      api
        .get(`/user/${activity.assigned_user_id}`)
        .then((r) => setCreatorUser(r.data));
    if (activity?.operator_id)
      api
        .get(`/user/${activity.operator_id}`)
        .then((r) => setAssignedUser(r.data));
  }, [activity]);

  useEffect(() => {
    if (!assignedUser || !editDraft) return;
  
    if (/^\d+$/.test(editDraft.operator_id)) {
      setEditDraft((prev) => ({
        ...prev,
        operator_id: toMention(assignedUser),
      }));
    }
  }, [assignedUser, editDraft]);

  /* censimento draft sync */
  useEffect(() => {
    setCensusDraft(censusData ?? { activity_id: id });
  }, [censusData, id]);

  const saveEditedActivity = async () => {
    const raw = editDraft.operator_id;
    const operatorId =
      raw && typeof raw === "string"
        ? Number(raw.match(/\((\d+)\)$/)?.[1] || raw)
        : Number(raw || 0) || null;

    try {
      await api.patch(`/activity/activities/${id}`, {
        activity_names: editDraft.activity_names,
        means: editDraft.means,
        priority: editDraft.priority,
        operator_id: operatorId,
        description: editDraft.description,
        start_date: startDateTime ? startDateTime.toISOString() : null,
        end_date: endDateTime ? endDateTime.toISOString() : null,
      });
      toast.success("Dati attività aggiornati");
      const { data } = await api.get(`/activity/activities/${id}`);
      setActivity(data);
      setIsEditing(false);
    } catch (error) {
      console.error('Errore salvataggio modifiche:', error);
      if (error.response?.status === 401) {
        toast.error("Sessione scaduta. Effettua nuovamente il login.");
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(`Errore nel salvataggio delle modifiche: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const updateStatus = async (newStatus) => {
    if (isRup) {
      toast.error("Non hai i permessi per modificare lo stato delle attività");
      return;
    }
    try {
      await api.patch(`/activity/activities/${id}`, { status: newStatus });
      setActivity((p) => ({ ...p, status: newStatus }));
      toast.success(`Stato aggiornato a "${newStatus}"`);
    } catch (error) {
      console.error('Errore aggiornamento stato:', error);
      if (error.response?.status === 401) {
        toast.error("Sessione scaduta. Effettua nuovamente il login.");
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(`Errore durante l'aggiornamento dello stato: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const uploadImages = async (filesArray, field) => {
    if (isRup) {
      toast.error("Non hai i permessi per caricare foto");
      return;
    }
    
    try {
      for (const file of filesArray) {
        const fd = new FormData();
        fd.append(field, file);
        await api.patch(`/activity/activities/${id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      const { data } = await api.get(`/activity/activities/${id}`);
      setActivity(data);
      toast.success("Immagini caricate con successo");
    } catch (error) {
      console.error('Errore caricamento foto:', error);
      if (error.response?.status === 401) {
        toast.error("Sessione scaduta. Effettua nuovamente il login.");
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(`Errore nel caricamento delle foto: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const deleteSingleExisting = async (field, idx, filename) => {
    if (isRup) {
      toast.error("Non hai i permessi per eliminare le foto");
      return;
    }
    
    try {
      await api.delete(
        `/activity/activities/${id}/images/${field}/${encodeURIComponent(
          filename
        )}`
      );
      setActivity((p) => {
        const u = { ...p };
        u[field] = u[field].filter((f) => f !== filename);
        return u;
      });
      toast.success("Foto eliminata");
    } catch (error) {
      console.error('Errore eliminazione foto:', error);
      if (error.response?.status === 401) {
        toast.error("Sessione scaduta. Effettua nuovamente il login.");
        // Rimuovi il token scaduto e reindirizza al login
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(`Errore nell'eliminazione della foto: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const downloadPdfReport = async () => {
    if (!pdfRef.current) return;

    // Attendi che il componente sia renderizzato
    await new Promise((r) => setTimeout(r, 600));

    // Prima di catturare, trova tutti gli elementi link per ottenere le loro posizioni
    const linkElements = pdfRef.current.querySelectorAll('.pdf-link-area');
    const linkPositions = [];

    linkElements.forEach(element => {
      const rect = element.getBoundingClientRect();
      const containerRect = pdfRef.current.getBoundingClientRect();
      linkPositions.push({
        url: element.dataset.linkUrl,
        index: element.dataset.linkIndex,
        x: rect.left - containerRect.left,
        y: rect.top - containerRect.top,
        width: rect.width,
        height: rect.height
      });
    });

    // Cattura l'HTML come immagine per il layout grafico
    const canvas = await html2canvas(pdfRef.current, {
      scale: 2, // Migliore qualità
      useCORS: true,
      logging: false,
    });

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Calcola dimensioni immagine mantenendo proporzioni
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Aggiungi l'immagine del layout grafico
    const imgData = canvas.toDataURL("image/png", 0.95);
    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);

    // Calcola il fattore di scala tra HTML e PDF
    const scaleX = imgWidth / pdfRef.current.offsetWidth;
    const scaleY = imgHeight / pdfRef.current.offsetHeight;

    // Aggiungi link cliccabili nelle posizioni corrette
    linkPositions.forEach(pos => {
      if (pos.url) {
        // Costruisci URL completo se necessario
        const linkUrl = pos.url.startsWith('http') ? pos.url : `${window.location.origin}${pos.url}`;

        // Converti le coordinate HTML in coordinate PDF
        const pdfX = pos.x * scaleX;
        const pdfY = pos.y * scaleY;
        const pdfWidth = pos.width * scaleX;
        const pdfHeight = pos.height * scaleY;

        // Aggiungi area cliccabile nel PDF
        pdf.link(pdfX, pdfY, pdfWidth, pdfHeight, { url: linkUrl });
      }
    });

    // Se l'immagine è più alta della pagina, gestisci le pagine extra
    if (imgHeight > pageHeight) {
      const numPages = Math.ceil(imgHeight / pageHeight);
      for (let i = 1; i < numPages; i++) {
        pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          0,
          -(pageHeight * i),
          imgWidth,
          imgHeight
        );

        // Ri-aggiungi i link per le pagine successive
        linkPositions.forEach(pos => {
          if (pos.url) {
            const linkUrl = pos.url.startsWith('http') ? pos.url : `${window.location.origin}${pos.url}`;
            const pdfX = pos.x * scaleX;
            const pdfY = (pos.y * scaleY) - (pageHeight * i);
            const pdfWidth = pos.width * scaleX;
            const pdfHeight = pos.height * scaleY;

            // Aggiungi solo se il link è visibile in questa pagina
            if (pdfY >= -pdfHeight && pdfY < pageHeight) {
              pdf.link(pdfX, pdfY, pdfWidth, pdfHeight, { url: linkUrl });
            }
          }
        });
      }
    }

    // Salva il PDF
    pdf.save(`report_activity_${activity.id}.pdf`);
  };

  const saveCensusData = async () => {
    if (isRup) {
      toast.error("Non hai i permessi per modificare i dati del censimento");
      return;
    }
    if (!censusDraft) return;
    setSavingCensus(true);
    try {
      const fd = new FormData();
      fd.append("specie", censusDraft.specie || "");
      fd.append("activity_id", id);
      fd.append("altezza", censusDraft.altezza ?? "");
      fd.append("diametro", censusDraft.diametro ?? "");
      fd.append("stato_salute", censusDraft.stato_salute || "");
      if (censusDraft.schedaPdf instanceof File)
        fd.append("schedaPdf", censusDraft.schedaPdf);
      if (censusDraft.documentoPdf instanceof File)
        fd.append("documentoPdf", censusDraft.documentoPdf);

      const isNew = !censusDraft.id;
      const url = isNew ? `/census` : `/census/${censusDraft.id}`;
      const method = isNew ? "post" : "put";

      const { data } = await api({
        method,
        url,
        data: fd,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCensusDraft(data);
      toast.success(isNew ? "Censimento creato" : "Censimento aggiornato");
    } catch (error) {
      console.error('Errore salvataggio censimento:', error);
      if (error.response?.status === 401) {
        toast.error("Sessione scaduta. Effettua nuovamente il login.");
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(`Errore durante il salvataggio del censimento: ${error.response?.data?.error || error.message}`);
      }
    } finally {
      setSavingCensus(false);
    }
  };

  const removeCensusPdf = async (field /* 'schedaPdf' | 'documentoPdf' */) => {
    if (!censusDraft) return;

    // 1. File ancora locale (oggetto File) → reset solo nello state
    if (!censusDraft.id || censusDraft[field] instanceof File) {
      setCensusDraft((prev) => ({ ...prev, [field]: null }));
      return;
    }

    try {
      // 2. Campo vero della colonna (snake_case)
      const dbField = field === "schedaPdf" ? "scheda_pdf" : "documento_pdf";

      // 3. Trova il path nel draft (snake preferito, camel fallback)
      const filePath = censusDraft[dbField] ?? censusDraft[field];
      if (!filePath) {
        toast.error("Path del file non trovato");
        return;
      }

      // 4. Estrai solo il filename, senza '/uploads/'
      const filename = filePath.replace(/^\/?uploads\//, "");
      const encoded = encodeURIComponent(filename);

      // 5. DELETE al backend
      await api.delete(`/census/${censusDraft.id}/pdf/${dbField}/${encoded}`);

      // 6. Pulisci entrambe le chiavi nello state
      setCensusDraft((prev) => ({
        ...prev,
        [field]: null, // camelCase
        [dbField]: null, // snake_case
      }));
      toast.success("File eliminato con successo");
    } catch (err) {
      console.error("Errore delete pdf:", err);
      toast.error("Impossibile eliminare il file");
    }
  };

  const formatAddress = (locInfo = "") => {
    const p = locInfo.split(",").map((s) => s.trim());
    const civico = p[0] || "";
    const via = p[1] || "";
    const città = p[4] || "";
    return via || civico || città ? `${via} ${civico}, ${città}`.trim() : "";
  };

  const beforeUrls = activity?.photo_before?.map(getUploadUrl) ?? [];
  const afterUrls = activity?.photo_after?.map(getUploadUrl) ?? [];
  const canClose = beforeUrls.length && afterUrls.length;

  const hasCensus =
    activity?.activity_names
      ?.map((n) => n.toLowerCase().replace(/\s/g, "_"))
      .includes("censimento_arboreo") ?? false;

  if (!activity) return null;
  const addressStr = formatAddress(activity.location_info);

  return (
    <div className="min-h-screen bg-global p-12">
      {/* header */}
      <header className="flex justify-between items-center mb-6">
        <div className="flex space-x-2">
          <Search onSearch={() => {}} defaultValue={addressStr} />
        </div>
        {hasCensus && (
          <div className="flex-1 flex justify-center">
            <ActivityTabBar activeTab={activeTab} onTabChange={setActiveTab} />
          </div>
        )}
        <div className="flex space-x-2 items-center">
          <UserBadge />
          <BackButton />
        </div>
      </header>

      {/* grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* mappa + foto */}
        <div className="space-y-4">
          <div className="max-w-[800px] max-h-[400px] overflow-hidden rounded-2xl">
            <Map
              position={mapPosition}
              setPosition={() => {}}
              onLocationInfo={() => {}}
            />
          </div>
          <div className="grid grid-cols-2 gap-6">
            <ImageUploadCard
              label="Prima"
              icon={iconPlus}
              disabled={isReadOnly}
              previewImages={beforeUrls}
              onFileSelect={(f) => uploadImages(f, "photo_before")}
              onRemove={(i, fn) => deleteSingleExisting("photo_before", i, fn)}
            />
            <ImageUploadCard
              label="Dopo"
              icon={iconPlus}
              disabled={isReadOnly}
              previewImages={afterUrls}
              onFileSelect={(f) => uploadImages(f, "photo_after")}
              onRemove={(i, fn) => deleteSingleExisting("photo_after", i, fn)}
            />
          </div>
        </div>

        {/* dettagli */}
        <div className="bg-primary rounded-2xl shadow p-6">
          {activeTab === "all" ? (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-4">
                {/* COL1 */}
                <div className="space-y-6">
                  {/* attività */}
                  <div>
                    {!isEditing ? (
                      <div>
                        <label className="block font-medium text-green-500 pt-2">
                          Attività
                        </label>
                        <div className="flex flex-wrap gap-2 text-white py-1">
                          {activity.activity_names.map((n) => (
                            <span
                              key={n}
                              className="bg-primary border-2 border-secondary rounded-full px-3 py-1 text-sm"
                            >
                              {getLabel(n, ACTIVITY_OPTIONS)}
                            </span>
                          ))}
                        </div>
                        {!isEditing && addressStr && (
                          <div className="text-white text-sm mt-1 ml-[2px]">
                            📍 {addressStr}
                          </div>
                        )}
                      </div>
                    ) : (
                      <ActivitySelector
                        value={editDraft.activity_names}
                        onChange={(v) =>
                          setEditDraft((p) => ({ ...p, activity_names: v }))
                        }
                      />
                    )}
                  </div>

                  {/* mezzi */}
                  <div>
                    {!isEditing ? (
                      <div>
                        <label className="block font-medium text-green-500">
                          Mezzi
                        </label>
                        <div className="flex flex-wrap gap-2 text-white py-1">
                          {activity.means.map((m) => (
                            <span
                              key={m}
                              className="bg-primary border-2 border-secondary rounded-full px-3 py-1 text-sm"
                            >
                              {getLabel(m, MEANS_OPTIONS)}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <MeansSelector
                        value={editDraft.means}
                        onChange={(v) =>
                          setEditDraft((p) => ({ ...p, means: v }))
                        }
                      />
                    )}
                  </div>

                  {/* priorità */}
                  <div>
                    <label className="block font-medium text-green-500">
                      Priorità
                    </label>
                    {!isEditing ? (
                      <div className="text-white py-1">{activity.priority}</div>
                    ) : (
                      <div className="flex gap-4 text-white">
                        {["Bassa", "Media", "Alta"].map((lvl) => (
                          <label key={lvl} className="flex items-center gap-1">
                            <input
                              type="radio"
                              checked={editDraft.priority === lvl}
                              onChange={() =>
                                setEditDraft((p) => ({ ...p, priority: lvl }))
                              }
                              className="appearance-none border-2 border-green-500 bg-primary
                                         checked:bg-green-500 rounded-full w-4 h-4"
                            />
                            {lvl}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* creator / operatore */}
                  <div>
                    <label className="block font-medium text-green-500">
                      Assegnato da
                    </label>
                    <div className="text-white py-1">
                      {creatorUser
                        ? `${creatorUser.firstName} ${creatorUser.lastName}`
                        : "—"}
                    </div>
                  </div>
                  <div>
                    {!isEditing ? (
                      <div>
                        <label className="block font-medium text-green-500">
                          Assegnato a
                        </label>
                        <div className="text-white py-1">
                          {assignedUser
                            ? `${assignedUser.firstName} ${assignedUser.lastName}`
                            : "—"}
                        </div>
                      </div>
                    ) : (
                      <OperatorMentionInput
                        value={editDraft.operator_id}
                        onChange={(v) =>
                          setEditDraft((p) => ({
                            ...p,
                            operator_id: v ? String(v) : "",
                          }))
                        }
                      />
                    )}
                  </div>
                </div>

                {/* COL2 */}
                <div className="space-y-8">
                  <div className="flex flex-wrap justify-end gap-2">
                    {activity.status === "pending" && (
                      <button
                        onClick={() => updateStatus("open")}
                        className="bg-green-600 text-white px-4 py-2 rounded-full hover:bg-green-700"
                      >
                        Apri attività
                      </button>
                    )}

                    {activity.status === "open" && !isEditing && (
                      <div className="flex items-center gap-2">
                        {/* Pulsante principale - Salva note */}
                        <button
                          onClick={handleSaveNotes}
                          className="bg-blue-500 text-white px-4 py-2 rounded-full hover:bg-blue-600"
                        >
                          Salva note
                        </button>
                        {/* Dropdown Azioni */}
                        <div className="relative">
                          <button
                            onClick={() => setShowActionsDropdown(!showActionsDropdown)}



  
                          className="bg-gray-500 text-white px-4 py-2 rounded-full hover:bg-gray-600 flex items-center gap-1"
                        >
                          Azioni
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {/* Dropdown menu */}
                        {showActionsDropdown && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                            <div className="py-1">
                              <button
                                onClick={handleEditActivity}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                ✏️ Modifica attività
                              </button>
                              {canClose && (
                                <button
                                  onClick={() => {
                                    if (hasCensus && !censusDraft?.id) {
                                      toast.error("Completa i dati del censimento prima di chiudere l'attività");
                                      return;
                                    }
                                    updateStatus("closed");
                                    setShowActionsDropdown(false);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  🔒 Chiudi attività
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        </div>
                      </div>
                    )}

                    {activity.status === "open" && isEditing && (
                      <>
                        <button
                          onClick={saveEditedActivity}
                          className="bg-green-600 text-white px-4 py-2 rounded-full hover:bg-green-700"
                        >
                          Salva
                        </button>
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setEditDraft({
                              activity_names: activity.activity_names,
                              means: activity.means,
                              priority: activity.priority,
                              operator_id: String(activity.operator_id ?? ""),
                              description: activity.description,
                            });
                            setStartDate(new Date(activity.start_date));
                            setEndDate(new Date(activity.end_date));
                          }}
                          className="bg-gray-500 text-white px-4 py-2 rounded-full hover:bg-gray-600"
                        >
                          Annulla
                        </button>
                      </>
                    )}

                    {/* Pulsanti per attività chiuse */}
                    {activity.status === "closed" && (
                      <div className="flex items-center gap-2">
                        {/* Pulsante download PDF - sempre disponibile */}
                        <button
                          onClick={downloadPdfReport}
                          className="bg-green-600 text-white px-4 py-2 rounded-full hover:bg-green-700"
                        >
                          📄 Scarica PDF
                        </button>

                        {/* Dropdown Azioni - solo per utenti non-RUP */}
                        {!isRup && !isEditing && (
                          <div className="relative">
                            <button
                              onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                              className="bg-gray-500 text-white px-4 py-2 rounded-full hover:bg-gray-600 flex items-center gap-1"
                            >
                              Azioni
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            
                            {/* Dropdown menu */}
                            {showActionsDropdown && (
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                                <div className="py-1">
                                  <button
                                    onClick={handleEditActivity}
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    ✏️ Modifica attività
                                  </button>
                                  <button
                                    onClick={handleSaveNotes}
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    💾 Salva note
                                  </button>
                                  <button
                                    onClick={handleReopenActivity}
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  >
                                    🔓 Riapri attività
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Pulsanti di modifica in edit mode - solo per utenti non-RUP */}
                        {!isRup && isEditing && (
                          <>
                            <button
                              onClick={saveEditedActivity}
                              className="bg-green-600 text-white px-4 py-2 rounded-full hover:bg-green-700"
                            >
                              Salva
                            </button>
                            <button
                              onClick={() => {
                                setIsEditing(false);
                                setEditDraft({
                                  activity_names: activity.activity_names,
                                  means: activity.means,
                                  priority: activity.priority,
                                  operator_id: String(activity.operator_id ?? ""),
                                  description: activity.description,
                                });
                                setStartDate(new Date(activity.start_date));
                                setEndDate(new Date(activity.end_date));
                              }}
                              className="bg-gray-500 text-white px-4 py-2 rounded-full hover:bg-gray-600"
                            >
                              Annulla
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {/* calendario preview */}
                    <div>
                      <label className="flex justify-start ml-5 text-green-500 mb-4">
                        Periodo
                      </label>
                      <div className="flex gap-12 justify-center mb-4">
                        <div className="flex flex-col items-center text-white">
                          <span>
                            {(
                              startDateTime || new Date("0000-01-01")
                            ).toLocaleDateString("it-IT", {
                              day: "2-digit",
                              month: "2-digit",
                            })}
                          </span>
                          <small>inizio</small>
                        </div>
                        <div className="flex flex-col items-center text-white">
                          <span>
                            {(
                              endDateTime || new Date("0000-01-01")
                            ).toLocaleDateString("it-IT", {
                              day: "2-digit",
                              month: "2-digit",
                            })}
                          </span>
                          <small>fine</small>
                        </div>
                        <div className="flex flex-col items-center text-white">
                          <span>
                            {startDateTime
                              ? startDateTime.getFullYear()
                              : "0000"}
                          </span>
                          <small>anno</small>
                        </div>
                      </div>

                      <div
                        onClick={
                          !isEditing ? (e) => e.preventDefault() : undefined
                        }
                        onMouseDown={
                          !isEditing ? (e) => e.preventDefault() : undefined
                        }
                        className={
                          !isEditing ? "pointer-events-none select-none" : ""
                        }
                      >
                        <DatePicker
                          inline
                          selectsRange
                          locale="it"
                          startDate={startDateTime}
                          endDate={endDateTime}
                          onChange={([start, end]) => {
                            setStartDate(start);
                            setEndDate(end);
                          }}
                          dateFormat="dd/MM/yyyy"
                          calendarClassName="bg-primary w-full flex justify-center border-none"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* note + descrizione */}
              <div className="space-y-4 mt-4">
                <label className="block font-medium text-white">
                  🗒 Note
                </label>
                <textarea
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                  placeholder="Aggiungi note per questa attività..."
                  className="w-full border p-3 rounded bg-secondary border-secondary text-white min-h-[100px] placeholder-gray-400"
                />
              </div>

              <label className="block font-medium text-white">
                Descrizione
              </label>
              {!isEditing ? (
                <div className="w-full border p-2 rounded bg-secondary border-secondary text-white min-h-[250px]">
                  {activity.description}
                </div>
              ) : (
                <textarea
                  value={editDraft.description}
                  onChange={(e) =>
                    setEditDraft((p) => ({ ...p, description: e.target.value }))
                  }
                  className="w-full border p-2 rounded bg-primary border-secondary text-white min-h-[250px]"
                />
              )}
            </div>
          ) : (
            <>
              {cLoading ? (
                <p className="text-white">Caricamento dati censimento...</p>
              ) : cError ? (
                <p className="text-red-500">Errore caricamento censimento</p>
              ) : (
                <>
                  <CensusDataDisplay
                    censusData={censusDraft}
                    onChange={setCensusDraft}
                    onRemoveFile={removeCensusPdf}
                    readOnly={isReadOnly}
                  />
                  {!isReadOnly && (
                    <div className="flex justify-end mt-4">
                      <button
                        onClick={saveCensusData}
                        disabled={savingCensus}
                        className={`px-4 py-2 rounded-full text-white ${
                          savingCensus
                            ? "bg-gray-400"
                            : "bg-green-600 hover:bg-green-700"
                        }`}
                      >
                        {savingCensus
                          ? "Salvataggio..."
                          : censusDraft?.id
                          ? "Aggiorna Censimento"
                          : "Crea Censimento"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* layout nascosto per PDF */}
      <PdfReportLayout
        ref={pdfRef}
        activity={activity}
        hideDates={isRup}
        mapPosition={mapPosition}
        startDateTime={startDateTime}
        endDateTime={endDateTime}
        creatorUser={creatorUser}
        assignedUser={assignedUser}
        getLabelFromValue={getLabel}
        ACTIVITY_OPTIONS={ACTIVITY_OPTIONS}
        MEANS_OPTIONS={MEANS_OPTIONS}
        getUploadUrl={getUploadUrl}
        MapComponent={Map}
      />
    </div>
  );
}
